import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClerkSupabaseClient } from '../../lib/supabase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { EmptyState } from '../../components/ui/empty-state';
import { KpiCard } from '../../components/ui/KpiCard';
import { StatusPill, StackedCell, MonoText, RowActions, IconButton } from '../../components/ui/YoolTable';
import { Download, RefreshCw, FileText, ShieldCheck, Clock, XCircle, Award, FileDown, Mail, X, User, CreditCard, Calendar, Building2, Users, ChevronDown, ChevronRight, Wallet, CheckCircle2, AlertCircle, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { CertificatesService } from '../../services/certificatesService';
import { resumenGrupo } from '../../lib/certificados/resumenGrupo';
import { walletService } from '../../services/walletService';
import { formatters } from '../../styles/tokens';

const YOOL_LOGO = 'https://yool.cl/assets/logoA002.svg';

/* ─── botón de acción con estilos inline (no depende de Tailwind JIT) ─ */
const BTN_BASE = {
  width: 32, height: 32, display: 'grid', placeItems: 'center',
  borderRadius: 8, cursor: 'pointer', border: '1px solid transparent',
  background: 'transparent', transition: 'all .14s ease',
};
function ActionBtn({ title, onClick, disabled, danger, children }) {
  const [h, setH] = React.useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        ...BTN_BASE,
        color: h ? (danger ? '#C0004A' : '#1A1A1A') : (danger ? '#C0004A' : '#6B7176'),
        border: h ? `1px solid ${danger ? '#F4B8C9' : '#E0E0E0'}` : '1px solid transparent',
        background: h ? (danger ? '#FCEDF1' : '#F7F8F9') : 'transparent',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/* ─── componente de grupo expandible ─────────────────────────────── */
const EMISION_BADGE = {
  pendiente_recepcion: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  emitido:             'bg-green-50 text-green-700 border border-green-200',
  error_envio:         'bg-red-50 text-red-700 border border-red-200',
  colectiva:           'bg-blue-50 text-blue-700 border border-blue-200',
  huerfano:            'bg-gray-100 text-gray-500 border border-gray-200',
};

const EMISION_LABEL = {
  pendiente_recepcion: 'Pendiente',
  emitido:             'Emitido',
  error_envio:         'Error envío',
  colectiva:           'Colectiva',
  huerfano:            'Huérfano',
};

function emisionSummary(certs) {
  const pending   = certs.filter(c => c.estadoEmision === 'pendiente_recepcion').length;
  const emitido   = certs.filter(c => c.estadoEmision === 'emitido').length;
  const errores   = certs.filter(c => c.estadoEmision === 'error_envio').length;
  const colectiva = certs.filter(c => !!c.polizaColectivaId).length;
  const huerfano  = certs.filter(c => !c.estadoEmision && !c.polizaColectivaId).length;
  if (colectiva === certs.length) return { label: 'Colectiva',  cls: EMISION_BADGE.colectiva };
  if (huerfano  === certs.length) return { label: 'Huérfano',   cls: EMISION_BADGE.huerfano  };
  if (errores   > 0)              return { label: `${errores} error${errores > 1 ? 'es' : ''}`, cls: EMISION_BADGE.error_envio };
  if (pending   > 0 && emitido > 0) return { label: `${pending}P · ${emitido}E`, cls: 'bg-amber-50 text-amber-700 border border-amber-200' };
  if (pending   > 0)              return { label: `${pending} pend.`, cls: EMISION_BADGE.pendiente_recepcion };
  if (emitido   > 0)              return { label: `${emitido} emit.`, cls: EMISION_BADGE.emitido };
  return { label: '—', cls: EMISION_BADGE.huerfano };
}

const SubscriptionGroup = ({ group, expanded, onToggle, getStatusBadge, getDaysUntilExpiry, handleViewDetails, handleDownloadCertificate, handleDownloadPdf, setEmailModal, setEmailTo, pdfLoadingId, regeneratingId, openRegenerateModal, setWalletModal, setWalletProvider, setWalletStep, setWalletEmail, setWalletResult, setWalletError, setCancelEmailModal, setCancelEmailTo }) => {
  const certs = group.certificates || [];
  const firstCert = certs[0];
  const badge = emisionSummary(certs);
  // La cabecera describe la ORDEN, que puede tener N líneas de distintas
  // compañías. Tomar `firstCert` mentía: la venta CE-2608-1365 + CE-2608-1366
  // se anunciaba como "YOOL DEMO 1" escondiendo su línea de BCI. `resumen` solo
  // afirma lo común; lo que varía se enumera o se cuenta.
  const resumen = resumenGrupo(certs);

  return (
    <>
      <tr
        className="cursor-pointer border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
        onClick={() => onToggle(group.group_key || group.subscription_id)}
      >
        <td className="w-6 text-gray-400 py-4 px-4">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </td>
        <td className="text-sm py-4 px-4">
          <StackedCell
            primary={resumen.cliente || (certs.length > 1 ? `${certs.length} titulares` : 'N/A')}
            secondary={
              group.group_tipo === 'pago'
                ? (firstCert?.contractCodigo || resumen.rut || 'N/A')
                : (resumen.rut || '—')
            }
          />
        </td>
        <td className="text-sm py-4 px-4">
          <StackedCell
            primary={resumen.companiasTexto || '—'}
            secondary={
              // Con más de un producto no hay UN plan que nombrar: se dice
              // cuántos son, y el detalle expandido los lista uno por uno.
              resumen.plan
              || (resumen.productos.length > 1
                ? `${resumen.productos.length} productos`
                : (group.group_tipo === 'pago'
                    ? `Póliza anual · ${firstCert?.contractCodigo || '—'}`
                    : `Suscripción · ${group.subscription_codigo || firstCert?.subscriptionCodigo || '—'}`))
            }
          />
        </td>
        <td className="text-sm py-4 px-4">
          {/* Un solo estado si las N líneas coinciden; si no, se muestran todos
              en vez de dar por buena la cobertura de la primera. */}
          {resumen.estados.length > 1 ? (
            <div className="flex flex-wrap gap-1">
              {certs
                .filter((c, i) => certs.findIndex(o => o.status === c.status) === i)
                .map(c => (
                  <span key={c.status}>
                    {getStatusBadge(c.status, c.expiryDate, c.estadoCertificado, c.coberturaHasta)}
                  </span>
                ))}
            </div>
          ) : (
            getStatusBadge(firstCert?.status, firstCert?.expiryDate, firstCert?.estadoCertificado, firstCert?.coberturaHasta)
          )}
        </td>
        <td className="text-sm py-4 px-4">
          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
            {badge.label}
          </span>
        </td>
        <td className="text-sm py-4 px-4 text-center">
          <span className="inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            {certs.length}
          </span>
        </td>
      </tr>

      {expanded && (
        <tr className="cursor-default">
          <td colSpan={6} className="bg-gray-50 dark:bg-gray-800/30 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative w-full overflow-auto">
              <table className="text-sm w-full border-collapse">
                <thead className="bg-gray-200 dark:bg-gray-700 border-y border-gray-300 dark:border-gray-600">
                  <tr>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Producto</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Plan</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Emisión</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Vencimiento</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Trazabilidad</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Estado</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Estado emisión</th>
                    <th className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((cert) => (
                    <tr key={cert.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="text-xs text-gray-600 dark:text-gray-400 py-3 px-4">
                        {cert.productName || '—'}
                      </td>
                      <td className="text-xs text-gray-600 dark:text-gray-400 py-3 px-4">
                        {cert.planName || '—'}
                      </td>
                      <td className="text-xs text-gray-600 dark:text-gray-400 py-3 px-4">
                        {fmtDate(cert.issueDate)}
                      </td>
                      <td className="text-xs text-gray-600 dark:text-gray-400 py-3 px-4">
                        {fmtDate(cert.expiryDate)}
                      </td>
                      <td className="text-xs py-3 px-4">
                        <div className="min-w-[380px]">
                          <MonoText>
                            {cert.quotationCodigo && cert.quotationCodigo !== '—' ? (
                              <Link to="/conversiones/simulaciones" className="text-[#007D6E] hover:underline">
                                {cert.quotationCodigo}
                              </Link>
                            ) : '—'}
                            <span className="text-muted-foreground"> - </span>
                            {cert.contractCodigo && cert.contractCodigo !== '—' ? (
                              <Link to="/conversiones/contrataciones" className="text-[#007D6E] hover:underline">
                                {cert.contractCodigo}
                              </Link>
                            ) : '—'}
                            <span className="text-muted-foreground"> - </span>
                            {cert.subscriptionCodigo && cert.subscriptionCodigo !== '—' ? (
                              <Link to="/conversiones/suscripciones" className="text-[#007D6E] hover:underline">
                                {cert.subscriptionCodigo}
                              </Link>
                            ) : '—'}
                            <span className="text-muted-foreground"> - </span>
                            {cert.certificateCodigo && cert.certificateCodigo !== '—' ? (
                              <span className="text-[#007D6E]">{cert.certificateCodigo}</span>
                            ) : '—'}
                            {cert.walletCodigo && (
                              <>
                                <span className="text-muted-foreground"> - </span>
                                <Link to="/wallet" className="inline-flex items-center gap-0.5 text-[#007D6E] hover:underline">
                                  <Wallet className="h-2.5 w-2.5" />
                                  {cert.walletCodigo}
                                </Link>
                              </>
                            )}
                          </MonoText>
                        </div>
                      </td>
                      <td className="text-xs py-3 px-4">
                        {getStatusBadge(cert.status, cert.expiryDate, cert.estadoCertificado, cert.coberturaHasta)}
                      </td>
                      <td className="text-xs py-3 px-4">
                        <EmisionBadge estadoEmision={cert.estadoEmision} />
                        {!cert.estadoEmision && <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="text-xs py-3 px-4">
                        <RowActions>
                          <IconButton icon="eye" title="Ver detalle" onClick={() => handleViewDetails(cert)} />
                          <ActionBtn
                            title="Descargar archivo"
                            onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(cert); }}
                            /* Se habilita con el `archivo_id`, NO con `archivo.url`:
                               los certificados viven en el bucket privado
                               `stempel-certificados`, donde la URL pública es NULL
                               a propósito. Exigirla dejaba el botón apagado para
                               TODO certificado emitido, con el archivo ahí mismo. */
                            disabled={!cert.archiveId}
                          >
                            <Download width={14} height={14} />
                          </ActionBtn>
                          <ActionBtn
                            title="Generar PDF"
                            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(cert.id, cert.serialNumber); }}
                            disabled={pdfLoadingId === cert.id}
                          >
                            {pdfLoadingId === cert.id
                              ? <RefreshCw width={14} height={14} style={{ animation: 'spin 1s linear infinite' }} />
                              : <FileDown width={14} height={14} />}
                          </ActionBtn>
                          <ActionBtn
                            title="Enviar por email"
                            onClick={(e) => { e.stopPropagation(); setEmailModal(cert.id); setEmailTo(cert.domain !== 'N/A' ? cert.domain : ''); }}
                          >
                            <Mail width={14} height={14} />
                          </ActionBtn>
                          <ActionBtn
                            title="Emitir Wallet Pass"
                            onClick={(e) => {
                              e.stopPropagation();
                              setWalletProvider('apple');
                              setWalletModal(cert);
                              setWalletStep(null);
                              setWalletEmail(cert.domain && cert.domain !== '—' && cert.domain !== 'N/A' ? cert.domain : '');
                              setWalletResult(null);
                              setWalletError(null);
                            }}
                          >
                            <Wallet width={14} height={14} />
                          </ActionBtn>
                          <IconButton
                            icon="refresh"
                            title="Regenerar certificado"
                            onClick={(e) => { e.stopPropagation(); openRegenerateModal(cert.id); }}
                          />
                          {cert.estadoCertificado && cert.estadoCertificado !== 'vigente' && (
                            <ActionBtn
                              title="Enviar email de baja"
                              onClick={(e) => { e.stopPropagation(); setCancelEmailModal(cert); setCancelEmailTo(cert.domain && cert.domain !== '—' && cert.domain !== 'N/A' ? cert.domain : ''); }}
                              danger
                            >
                              <XCircle width={14} height={14} />
                            </ActionBtn>
                          )}
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Caché módulo-nivel: sobrevive navegación sin re-fetch visible
let _certCache = [];

/* ─── helpers ─────────────────────────────────────────────────── */
const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-slate-800 dark:text-slate-200 break-all">{value || '—'}</span>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#007D6E]" />
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</h3>
    </div>
    {children}
  </div>
);

const EMISION_CFG = {
  pendiente_envio:     { label: 'Pendiente envío',    bg: '#FEF3C7', color: '#92400E' },
  pendiente_recepcion: { label: 'Pendiente recepción', bg: '#DBEAFE', color: '#1E40AF' },
  emitido:             { label: 'Emitido',             bg: '#D1FAE5', color: '#065F46' },
  error_envio:         { label: 'Error envío',         bg: '#FEE2E2', color: '#991B1B' },
};

const EmisionBadge = ({ estadoEmision }) => {
  if (!estadoEmision) return null;
  const cfg = EMISION_CFG[estadoEmision];
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 600,
      padding: '2px 7px',
      borderRadius: 9999,
      backgroundColor: cfg.bg,
      color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

const fmtDate = (d) => {
  if (!d || d === 'N/A') return '—';
  try { return formatters.formatDate(d); } catch { return d; }
};

const fmtUF = (value) => {
  if (value == null || value === 'N/A' || value === '') return '—';
  try { return formatters.formatUF(value); } catch { return `${value} UF`; }
};

const fmtUFDetailed = (value) => {
  if (value == null || value === 'N/A' || value === '') return '—';
  return `${Number(value).toFixed(2)} UF`;
};

const buildFullName = (...parts) => parts.filter(Boolean).join(' ').trim() || '—';

const parseJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return typeof value === 'object' ? value : {};
};

const toNumberOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeKey = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/\s+/g, '_')
  .trim();


/* ─── component ────────────────────────────────────────────────── */
const CertificatesList = () => {
  const { session } = useSession();
  const supabaseClient = useMemo(() => createClerkSupabaseClient(session), [session]);

  const [certificates, setCertificates]       = useState(_certCache);
  const [loading, setLoading]                 = useState(_certCache.length === 0);
  const [refreshing, setRefreshing]           = useState(false);
  const [searchParams, setSearchParams]       = useSearchParams();
  const searchTerm   = searchParams.get('search')  || '';
  const statusFilter   = searchParams.get('status')   || 'all';
  const emisionFilter  = searchParams.get('emision')  || 'all';
  const companiaFilter = searchParams.get('compania') || 'all';
  const setSearchTerm     = (v) => setSearchParams(p => { const n = new URLSearchParams(p); if (v) n.set('search', v); else n.delete('search'); return n; }, { replace: true });
  const setStatusFilter   = (v) => setSearchParams(p => { const n = new URLSearchParams(p); if (v && v !== 'all') n.set('status', v); else n.delete('status'); return n; }, { replace: true });
  const setEmisionFilter  = (v) => setSearchParams(p => { const n = new URLSearchParams(p); if (v && v !== 'all') n.set('emision', v); else n.delete('emision'); return n; }, { replace: true });
  const setCompaniaFilter = (v) => setSearchParams(p => { const n = new URLSearchParams(p); if (v && v !== 'all') n.set('compania', v); else n.delete('compania'); return n; }, { replace: true });
  const [expandedRows, setExpandedRows]       = useState(new Set());
  const [groupBy, setGroupBy]                 = useState('suscripcion'); // 'suscripcion' | 'compania'
  const { toast }                             = useToast();

  // PDF actions
  const [pdfLoadingId, setPdfLoadingId]       = useState(null);
  const [emailModal,   setEmailModal]         = useState(null);
  const [emailTo,      setEmailTo]            = useState('');
  const [sendingEmail, setSendingEmail]       = useState(false);
  const [regeneratingId, setRegeneratingId]   = useState(null);

  // Cancellation email (certificate anulado)
  const [cancelEmailModal,   setCancelEmailModal]   = useState(null);  // cert object or null
  const [cancelEmailTo,      setCancelEmailTo]      = useState('');
  const [cancelEmailSending, setCancelEmailSending] = useState(false);

  // Regenerate modal
  const [regenerateModal, setRegenerateModal] = useState(null);

  // Wallet
  const [walletModal,    setWalletModal]    = useState(null);   // cert object or null
  const [walletProvider, setWalletProvider] = useState('apple');
  const [walletEmail,    setWalletEmail]    = useState('');
  const [walletStep,     setWalletStep]     = useState(null);   // null | 'issuing' | 'done' | 'error'
  const [walletSubstep,  setWalletSubstep]  = useState(0);      // 0-2, cosmetic animation
  const [walletResult,   setWalletResult]   = useState(null);
  const [walletError,    setWalletError]    = useState(null);
  const walletTimers = useRef([]);

  // Detail modal
  const [detailOpen,       setDetailOpen]     = useState(false);
  const [detailLoading,    setDetailLoading]  = useState(false);
  const [certificateDetail, setCertificateDetail] = useState(null);
  const [detailCertBase,   setDetailCertBase] = useState(null);  // basic cert from list
  const [detailFallback,   setDetailFallback] = useState(null);

  useEffect(() => { if (session) fetchCertificates(); }, [session]);

  // Bloquear scroll cuando algún modal está abierto
  useEffect(() => {
    if (detailOpen || emailModal || regenerateModal || cancelEmailModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [detailOpen, emailModal, regenerateModal, cancelEmailModal]);

  /* ── fetch list ── */
  const fetchCertificates = async ({ background = false } = {}) => {
    try {
      // Si ya hay datos, refrescar sin vaciar la tabla (sin parpadeo)
      if (background || _certCache.length > 0) setRefreshing(true);
      else setLoading(true);

      const [raw, walletPassesRes] = await Promise.all([
        CertificatesService.getCertificates({}, supabaseClient),
        walletService.listPasses({ limit: 1000 }).catch(() => ({ data: [] })),
      ]);
      const passByCertId = new Map((walletPassesRes.data ?? []).map(p => [p.certificado_id ?? p.policy_id, p]));

      const subscriptionIds = [...new Set((raw || []).map(cert => cert?.subscription_id).filter(Boolean))];

      // Recolectar todos los contratacion_ids de los certificados (para obtener teléfonos)
      const certificateContractIds = [...new Set(
        (raw || [])
          .flatMap(group => group.certificates || [])
          .map(cert => cert.subscription?.contratacion_id)
          .filter(Boolean)
      )];

      let subscriptionsById = new Map();
      let cargasCountByContractId = new Map();
      let contractsById = new Map();
      let contractRawById = new Map();
      let quotationsById = new Map();
      let phonesByContractId = new Map();

      if (subscriptionIds.length > 0) {
        const { data: subscriptionsData } = await supabaseClient
          .from('company_suscripciones')
          .select('id, codigo, contratacion_id')
          .in('id', subscriptionIds);

        subscriptionsById = new Map((subscriptionsData || []).map(subscription => [subscription.id, subscription]));

        const contractIds = [...new Set(
          (subscriptionsData || [])
            .map(subscription => subscription?.contratacion_id)
            .filter(Boolean)
        )];

        if (contractIds.length > 0) {
          const [{ data: contractData }, { data: contractChargesData }, { data: contractViewData }] = await Promise.all([
            supabaseClient
              .from('contrataciones')
              .select('id, datos_titular')
              .in('id', contractIds),
            supabaseClient
              .from('contratacion_cargas')
              .select('id, contratacion_id')
              .in('contratacion_id', contractIds),
            supabaseClient
              .from('company_contrataciones')
              .select('id, codigo, cotizacion_id, datos_titular')
              .in('id', contractIds)
          ]);

          const groupedCharges = (contractChargesData || []).reduce((acc, charge) => {
            const contractId = charge?.contratacion_id;
            if (!contractId) return acc;
            acc[contractId] = (acc[contractId] || 0) + 1;
            return acc;
          }, {});

          cargasCountByContractId = new Map(
            contractIds.map((id) => [id, groupedCharges[id] || 0])
          );

          contractRawById = new Map((contractData || []).map(contract => [contract.id, contract]));
          contractsById = new Map((contractViewData || []).map(contract => [contract.id, contract]));

          const quotationIds = [...new Set(
            (contractViewData || [])
              .map(contract => contract?.cotizacion_id)
              .filter(Boolean)
          )];

          if (quotationIds.length > 0) {
            const { data: quotationData } = await supabaseClient
              .from('company_cotizaciones')
              .select('id, codigo')
              .in('id', quotationIds);

            quotationsById = new Map((quotationData || []).map(quotation => [quotation.id, quotation]));
          }
        }
      }

      // Obtener teléfonos de company_contrataciones para certificados nuevos
      if (certificateContractIds.length > 0) {
        const { data: companyContractData } = await supabaseClient
          .from('company_contrataciones')
          .select('id, datos_titular')
          .in('id', certificateContractIds);

        if (companyContractData) {
          companyContractData.forEach(contract => {
            const titularData = parseJsonObject(contract.datos_titular);
            if (titularData?.telefono) {
              phonesByContractId.set(contract.id, titularData.telefono);
            }
          });
        }
      }

      // Procesar datos agrupados por suscripción
      const mapped = (raw || []).map(group => {
        // El grupo contiene: subscription_codigo, subscription_id, certificates (array)
        const certificates = group.certificates || [];

        // Mapear cada certificado en el grupo
        const mappedCerts = certificates.map(cert => {
          // Cadena: cert → subscription → contratacion / compania
          const sub          = cert.subscription || {};
          const contratacion = sub.contratacion  || {};
          const compania     = sub.compania      || {};
          const fallbackSubscription = subscriptionsById.get(cert.subscription_id) || {};
          const contractId = contratacion.id || fallbackSubscription?.contratacion_id || cert.subscription?.contratacion_id || null;
          const fallbackContract = contractsById.get(contractId) || {};
          const fallbackContractRaw = contractRawById.get(contractId) || {};
          const quotationId = contratacion?.cotizacion?.id || fallbackContract?.cotizacion_id || null;
          const fallbackQuotation = quotationsById.get(quotationId) || {};
          const titularData = parseJsonObject(fallbackContractRaw?.datos_titular);
          const companyContractTitular = parseJsonObject(fallbackContract?.datos_titular);
          const phoneFromMap = phonesByContractId.get(contractId);

          // Estado desde subscription.status
          const subStatus = (sub.status || '').toLowerCase();
          let status = 'pending';
          if (['activa', 'confirmada'].includes(subStatus))                       status = 'active';
          else if (['cancelled', 'expired', 'inactive', 'fallida', 'error'].includes(subStatus)) status = 'revoked';

          // Cuando no hay contratación (flujo simulate / poliza colectiva sin titular)
          // usar compania + plan como identificador visual
          const displayName = contratacion.nombre
            || cert.subscription?.contratacion?.nombre
            || compania.nombre_corto
            || sub.plan_name
            || '—';
          const displayEmail = contratacion.mail
            || cert.subscription?.contratacion?.mail
            || sub.plan_name
            || '—';

          return {
            id:             cert.id,
            clientName:     displayName,
            domain:         displayEmail,
            clientPhone:    phoneFromMap || companyContractTitular?.telefono || titularData?.telefono || titularData?.celular || titularData?.mobile || '—',
            status,
            issueDate:      cert.fecha_emision           || null,
            expiryDate:     cert.fecha_termino_cobertura || sub.end_date || null,
            serialNumber:   cert.codigo                  || cert.id,
            subscriptionId: cert.subscription_id,
            subscriptionCodigo: cert.subscriptionCodigo || fallbackSubscription?.codigo || group.subscription_codigo || '—',
            paymentId:      cert.pago_id,
            amount:         sub.amount                   || 0,
            planName:       cert.planName || sub.plan_name || '—',
            productName:    cert.productName || '—',
            companyName:    compania.nombre_corto        || '—',
            companyLogo:    compania.logo_url            || null,
            clientRut:      contratacion.rut             || cert.subscription?.contratacion?.rut || '—',
            contractCodigo: cert.contractCodigo || contratacion.codigo || fallbackContract?.codigo || '—',
            contractId,
            quotationCodigo: cert.quotationCodigo || fallbackQuotation?.codigo || '—',
            quotationId,
            certificateCodigo: cert.certificateCodigo || cert.codigo || '—',
            archiveId:      cert.archivo_id,
            archivo:        cert.archivo || null,
            archiveUrl:     cert.archivo?.url  ?? null,
            archiveType:    cert.archivo?.tipo ?? null,
            numCargas:      cargasCountByContractId.get(contractId) ?? (sub.contratacion?.contratacion_cargas || []).length,
            walletCodigo:   passByCertId.get(cert.id)?.policy_number ?? null,
            estadoCertificado: cert.estadoCertificado || 'vigente',
            coberturaHasta: cert.coberturaHasta || null,
            estadoEmision: cert.estadoEmision || null,
            polizaColectivaId: cert.polizaColectivaId || null,
          };
        });

        return {
          group_key:         group.group_key,
          group_tipo:        group.group_tipo || 'suscripcion',
          subscription_codigo: group.subscription_codigo,
          subscription_id:   group.subscription_id,
          orden_pago_id:     group.orden_pago_id,
          certificates: mappedCerts
        };
      });

      _certCache = mapped;
      setCertificates(mapped);
    } catch (error) {
      console.error('Error al cargar certificados:', error);
      toast({ title: 'Error', description: 'Error al cargar los certificados', variant: 'destructive' });
    } finally {
      if (background) setRefreshing(false);
      else            setLoading(false);
    }
  };

  /* ── detail modal ── */
  const handleViewDetails = async (certificate) => {
    setDetailCertBase(certificate);
    setCertificateDetail(null);
    setDetailFallback(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const detail = await CertificatesService.getCertificateDetail(certificate.id, supabaseClient);
      const subscriptionId = detail?.subscription_id || certificate?.subscriptionId || null;

      let subscription = null;
      let contratacion = null;
      let cotizacion = null;

      // Get subscription to find contratacion
      if (subscriptionId) {
        const { data: subData } = await supabaseClient
          .from('subscriptions')
          .select('id, contratacion_id, status, amount, plan_name, start_date, end_date')
          .eq('id', subscriptionId)
          .maybeSingle();

        subscription = subData || null;
      }

      const contratacionId = subscription?.contratacion_id || certificate?.contractId || null;

      // Get contratacion with cargas
      if (contratacionId) {
        const { data: contData } = await supabaseClient
          .from('contrataciones')
          .select(`
            id,
            nombre,
            mail,
            rut,
            codigo,
            datos_titular,
            cotizacion_id,
            contratacion_cargas(
              id,
              nombre,
              apellido_paterno,
              apellido_materno,
              rut,
              tipo,
              edad
            )
          `)
          .eq('id', contratacionId)
          .maybeSingle();

        contratacion = contData || null;
      }

      const cotizacionId = contratacion?.cotizacion_id || null;

      // Get cotizacion with resumen (has all pricing breakdown)
      if (cotizacionId) {
        const { data: cotData } = await supabaseClient
          .from('cotizaciones')
          .select('id, codigo, resumen, plan_id')
          .eq('id', cotizacionId)
          .maybeSingle();

        cotizacion = cotData || null;
      }

      setCertificateDetail(detail);
      setDetailFallback({
        subscription,
        contratacion,
        cotizacion,
      });
    } catch (err) {
      console.error('Error loading detail:', err);
      toast({ title: 'Error', description: 'No se pudo cargar el detalle', variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setCertificateDetail(null);
    setDetailCertBase(null);
    setDetailFallback(null);
  };

  /* ── status badge ── */
  const getStatusBadge = (status, expiryDate, estadoCert, coberturaHasta) => {
    const cfgAnulado = {
      anulado_cancelacion: { label: 'Cancelado',          estado: 'cancelado' },
      anulado_impago:      { label: 'Anulado por impago', estado: 'error'     },
      expirado:            { label: 'Expirado',           estado: 'expirado'  },
    };
    if (estadoCert && cfgAnulado[estadoCert]) {
      const c = cfgAnulado[estadoCert];
      const hasta = coberturaHasta ? ` · cubierto hasta ${fmtDate(coberturaHasta)}` : '';
      return <StatusPill estado={c.estado}>{c.label}{hasta}</StatusPill>;
    }
    const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
    let actualStatus = status;
    if (actualStatus === 'active' && daysUntilExpiry <= 30) actualStatus = 'expiring_soon';
    else if (actualStatus === 'active' && daysUntilExpiry < 0) actualStatus = 'expired';

    const cfg = {
      active:        { label: 'Activo',     estado: 'active'    },
      expiring_soon: { label: 'Por Vencer', estado: 'pendiente' },
      expired:       { label: 'Expirado',   estado: 'expirado'  },
      revoked:       { label: 'Revocado',   estado: 'inactivo'  },
    };
    const c = cfg[actualStatus] || { label: actualStatus, estado: 'pendiente' };
    return <StatusPill estado={c.estado}>{c.label}</StatusPill>;
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate || expiryDate === 'N/A') return 9999;
    return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  /* ── certificate actions ── */
  const openRegenerateModal = (certificateId) => {
    setRegenerateModal(certificateId);
  };

  const closeRegenerateModal = () => {
    setRegenerateModal(null);
  };

  const confirmRegenerateCertificate = async () => {
    const certificateId = regenerateModal;
    closeRegenerateModal();

    setRegeneratingId(certificateId);
    try {
      try {
        // Regenera stempel, que es quien emite. Además deja vinculado el
        // `archivo_id` con service role: repetir ese UPDATE desde el browser
        // solo agregaba una vía de falla (RLS) sobre algo ya resuelto.
        await CertificatesService.regenerateCertificateWithCondicionado(certificateId);
      } catch (pdfErr) {
        toast({ title: 'Error', description: 'Error al generar el PDF: ' + pdfErr.message, variant: 'destructive' });
        throw pdfErr;
      }

      try {
        await fetchCertificates({ background: true });
        toast({ title: 'Éxito', description: 'Certificado regenerado exitosamente' });
      } catch (fetchErr) {
        console.error('Error al recargar certificados:', fetchErr);
        toast({ title: 'Advertencia', description: 'Certificado regenerado pero hubo un error al recargar la lista', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Error al regenerar el certificado', variant: 'destructive' });
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDownloadCertificate = async (certificate) => {
    try {
      // Basta el `archivo_id`: el proxy resuelve bucket + path del lado del
      // servidor. `archivo.url` viene NULL en el bucket privado y exigirla acá
      // era lo que respondía "Archivo no disponible" teniéndolo disponible.
      if (!certificate.archiveId) {
        toast({ title: 'Error', description: 'Este certificado todavía no tiene archivo emitido', variant: 'destructive' });
        return;
      }
      // Descargar el archivo como blob a través del proxy (bucket certificados es privado)
      const token = await session?.getToken();
      const filename = `certificado_${certificate.certificateCodigo || certificate.id}`;
      const response = await fetch(`/api/certificates/download?id=${certificate.archiveId}&name=${encodeURIComponent(filename)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error('Error descargando archivo');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Descarga completada' });
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Error al descargar el certificado', variant: 'destructive' });
    }
  };

  const handleDownloadPdf = async (certificateId, serialNumber) => {
    setPdfLoadingId(certificateId);
    try {
      await CertificatesService.downloadCertificatePdf(certificateId, `certificado-${serialNumber}.pdf`);
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'No se pudo generar el PDF', variant: 'destructive' });
    } finally {
      setPdfLoadingId(null);
    }
  };

  // Animate substeps while issuing
  useEffect(() => {
    walletTimers.current.forEach(clearTimeout);
    walletTimers.current = [];
    if (walletStep !== 'issuing') { setWalletSubstep(0); return; }
    setWalletSubstep(0);
    const t1 = setTimeout(() => setWalletSubstep(1), 900);
    const t2 = setTimeout(() => setWalletSubstep(2), 2100);
    walletTimers.current = [t1, t2];
    return () => { walletTimers.current.forEach(clearTimeout); };
  }, [walletStep]);

  const closeWalletModal = () => {
    setWalletModal(null);
    setWalletStep(null);
    setWalletEmail('');
    setWalletResult(null);
    setWalletError(null);
  };

  const handleWalletIssue = async () => {
    if (!walletModal) return;
    setWalletStep('issuing');
    setWalletResult(null);
    setWalletError(null);
    try {
      const cert = walletModal;

      // Fetch real data from DB: insurance_plan, insurance_type, policy_number
      let insurancePlan = null;
      let insuranceType = null;
      let policyNumber = null;

      // polizas_colectivas.numero_poliza:
      // 1. via certificados.poliza_colectiva_id (si está poblado)
      // 2. fallback: certificados.precio_base_id → precios_base.poliza_colectiva_id
      const { data: certRow } = await supabaseClient
        .from('certificados')
        .select('poliza_colectiva_id, precio_base_id')
        .eq('id', cert.id)
        .maybeSingle();

      // Resuelve numero_poliza + wallet_back_config + plan_id desde polizas_colectivas
      let backConfig = null;
      const resolvePolizaColectiva = async (polizaColectivaId) => {
        if (!polizaColectivaId) return null;
        const { data } = await supabaseClient
          .from('polizas_colectivas')
          .select('numero_poliza, wallet_back_config, plan_id')
          .eq('id', polizaColectivaId)
          .maybeSingle();
        return data ?? null;
      };

      let polizaData = null;
      if (certRow?.poliza_colectiva_id) {
        polizaData = await resolvePolizaColectiva(certRow.poliza_colectiva_id);
      }
      if (!polizaData?.numero_poliza && certRow?.precio_base_id) {
        const { data: precioBase } = await supabaseClient
          .from('precios_base')
          .select('poliza_colectiva_id')
          .eq('id', certRow.precio_base_id)
          .maybeSingle();
        polizaData = await resolvePolizaColectiva(precioBase?.poliza_colectiva_id);
      }

      policyNumber = polizaData?.numero_poliza ?? null;
      backConfig = polizaData?.wallet_back_config ?? null;
      // Último fallback: usar código del certificado
      if (!policyNumber) policyNumber = cert.serialNumber || cert.id.slice(-8).toUpperCase();

      // planes.nombre + producto.ramo + producto.nombre via cotizaciones.plan_id
      let productName = null;
      if (cert.quotationId) {
        const { data: quotData } = await supabaseClient
          .from('cotizaciones')
          .select('plan_id')
          .eq('id', cert.quotationId)
          .maybeSingle();
        if (quotData?.plan_id) {
          const { data: planData } = await supabaseClient
            .from('planes')
            .select('nombre, producto_id')
            .eq('id', quotData.plan_id)
            .maybeSingle();
          if (planData?.nombre) insurancePlan = planData.nombre;
          if (planData?.producto_id) {
            const { data: prodData } = await supabaseClient
              .from('producto')
              .select('ramo, nombre')
              .eq('id', planData.producto_id)
              .maybeSingle();
            if (prodData?.ramo) insuranceType = prodData.ramo;
            if (prodData?.nombre) productName = prodData.nombre;
          }
        }
      }

      // Fallback: polizas_colectivas.plan_id → planes → producto.ramo + nombre
      if (!insuranceType && polizaData?.plan_id) {
        const { data: planData } = await supabaseClient
          .from('planes')
          .select('nombre, producto_id')
          .eq('id', polizaData.plan_id)
          .maybeSingle();
        if (planData?.nombre && !insurancePlan) insurancePlan = planData.nombre;
        if (planData?.producto_id) {
          const { data: pd } = await supabaseClient
            .from('producto')
            .select('ramo, nombre')
            .eq('id', planData.producto_id)
            .maybeSingle();
          if (pd?.ramo) insuranceType = pd.ramo;
          if (pd?.nombre) productName = pd.nombre;
        }
      }

      const COV_MAP = { active: 'active', revoked: 'canceled', pending: 'inactive' };
      const policyData = {
        id: cert.id,
        policy_number: policyNumber,
        certificate_number: cert.serialNumber || cert.id.slice(-8).toUpperCase(),
        holder_name: cert.clientName || 'Sin nombre',
        holder_rut: cert.clientRut !== '—' ? cert.clientRut : null,
        insurance_company: cert.companyName !== '—' ? cert.companyName : 'Yool',
        insurance_type: insuranceType || 'vida',
        insurance_plan: insurancePlan || cert.planName || 'Plan Yool',
        product_name: productName || null,
        coverage_status: COV_MAP[cert.status] || 'active',
        payment_status: 'paid',
        claim_status: 'none',
        assistance_status: 'idle',
        start_date: cert.issueDate ? String(cert.issueDate).split('T')[0] : new Date().toISOString().split('T')[0],
        end_date: cert.expiryDate ? String(cert.expiryDate).split('T')[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        wallet_pass_installed: false,
        ...(backConfig ? { back_config: backConfig } : {}),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const res = await walletService.issuePass(cert.id, walletProvider, policyData, walletEmail || null);
      setWalletResult(res);
      setWalletStep('done');
    } catch (err) {
      setWalletError(err?.message || 'No se pudo emitir el pass');
      setWalletStep('error');
    }
  };

  const handleSendPdfEmail = async () => {
    if (!emailModal || !emailTo) return;
    setSendingEmail(true);
    try {
      await CertificatesService.generateAndEmailPdf(emailModal, emailTo);
      toast({ title: 'Email enviado', description: `PDF enviado a ${emailTo}` });
      setEmailModal(null);
      setEmailTo('');
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'No se pudo enviar el email', variant: 'destructive' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendCancelEmail = async () => {
    if (!cancelEmailModal || !cancelEmailTo) return;
    setCancelEmailSending(true);
    try {
      const res = await fetch('/api/certificates/send-cancelacion-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificateId: cancelEmailModal.id,
          emailTo: cancelEmailTo,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `Email sending failed: ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      toast({ title: 'Email de baja enviado', description: `Enviado a ${data?.email_sent_to || cancelEmailTo}` });
      setCancelEmailModal(null);
      setCancelEmailTo('');
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'No se pudo enviar el email de baja', variant: 'destructive' });
    } finally {
      setCancelEmailSending(false);
    }
  };

  /* ── expandable rows ── */
  const toggleExpandedRow = (subscriptionId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(subscriptionId)) {
        next.delete(subscriptionId);
      } else {
        next.add(subscriptionId);
      }
      return next;
    });
  };

  /* ── filtered data ── */
  // Obtener todos los certificados individuales de los grupos
  const allIndividualCerts = certificates.flatMap(group => group.certificates || []);

  // Filtrar grupos basado en sus certificados
  const filteredCertificates = certificates.filter(group => {
    const certs = group.certificates || [];
    return certs.some(cert => {
      const serial = cert.serialNumber ? String(cert.serialNumber) : '';
      const subscriptionCode = cert.subscriptionCodigo ? String(cert.subscriptionCodigo) : '';
      const contractCode = cert.contractCodigo ? String(cert.contractCodigo) : '';
      const quotationCode = cert.quotationCodigo ? String(cert.quotationCodigo) : '';
      const matchesSearch = (cert.clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (cert.domain     || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            serial.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            subscriptionCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            contractCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            quotationCode.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;
      const matchesEmision = (() => {
        if (emisionFilter === 'all') return true;
        if (emisionFilter === 'huerfano') return !cert.estadoEmision && !cert.polizaColectivaId;
        if (emisionFilter === 'colectiva') return !!cert.polizaColectivaId;
        return cert.estadoEmision === emisionFilter;
      })();
      const matchesCompania = companiaFilter === 'all' || cert.companyName === companiaFilter;
      return matchesSearch && matchesStatus && matchesEmision && matchesCompania;
    });
  });

  const expiringCount    = allIndividualCerts.filter(c => { const d = getDaysUntilExpiry(c.expiryDate); return d <= 30 && d > 0; }).length;
  const expiredCount     = allIndividualCerts.filter(c => getDaysUntilExpiry(c.expiryDate) < 0).length;
  const pendienteRendir  = allIndividualCerts.filter(c => c.estadoEmision === 'pendiente_recepcion').length;
  const emitidoCount     = allIndividualCerts.filter(c => c.estadoEmision === 'emitido').length;
  const colectivaCount   = allIndividualCerts.filter(c => !!c.polizaColectivaId).length;
  const huerfanoCount    = allIndividualCerts.filter(c => !c.estadoEmision && !c.polizaColectivaId).length;

  const companiaOptions = useMemo(() => {
    const names = [...new Set(allIndividualCerts.map(c => c.companyName).filter(n => n && n !== '—'))];
    return names.sort();
  }, [allIndividualCerts]);

  const certsByCompania = useMemo(() => {
    const allFiltered = filteredCertificates.flatMap(g => g.certificates || []);
    const map = new Map();
    for (const cert of allFiltered) {
      const key = cert.companyName || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(cert);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredCertificates]);

  /* ── detail modal data helpers ── */


  /* ── detail modal data helpers ── */
  // Real data from tables (via detailFallback set in handleViewDetails)
  const detailSub = detailFallback?.subscription || {};
  const detailContratacion = detailFallback?.contratacion || {};
  const detailCotizacion = detailFallback?.cotizacion || {};

  // Real data from contrataciones table
  const detailCargas = detailContratacion?.contratacion_cargas || [];
  const titularRaw = parseJsonObject(detailContratacion?.datos_titular || {});

  // Parse resumen from cotizaciones table (has precise pricing breakdown)
  const cotizacionResumen = parseJsonObject(detailCotizacion?.resumen || {});

  // Extract primas from resumen structure: primaCalculada.primabase
  const primaBase = cotizacionResumen?.primaCalculada?.primabase || {};
  const primaTotal = cotizacionResumen?.primaCalculada?.primatotal || {};
  const cargasFromResumen = primaBase?.cargas || [];

  // Titular prima
  const titularPrimaUF = toNumberOrNull(primaBase?.titular) ?? null;

  // Total prima
  const totalPrimaResumenUF = toNumberOrNull(primaTotal?.total) ?? null;

  // Map cargas with primas from resumen
  const primasCargas = detailCargas.map((carga) => {
    const resumenCarga = cargasFromResumen.find(rc => {
      const tipoCargaNorm = normalizeKey(rc.tipocarga || '');
      const cargaTipo = normalizeKey(carga.tipo || '');
      return tipoCargaNorm.includes(cargaTipo) || cargaTipo.includes(tipoCargaNorm);
    });

    return {
      carga,
      primaUF: toNumberOrNull(resumenCarga?.primaUF) ?? null,
      tipoCarga: resumenCarga?.tipocarga || carga.tipo || '—',
    };
  });

  // Build detail info
  const detailNombre = buildFullName(
    titularRaw?.nombres || titularRaw?.nombre,
    titularRaw?.apellido_paterno,
    titularRaw?.apellido_materno
  ) || detailContratacion?.nombre || detailCertBase?.clientName || '—';

  const detailRut = titularRaw?.rut || detailContratacion?.rut || detailCertBase?.clientRut || '—';
  const detailEmail = titularRaw?.email || detailContratacion?.mail || detailCertBase?.domain || '—';
  const detailCompanyName = detailCertBase?.companyName || '—';
  const detailPlanName = detailCertBase?.planName || '—';
  const detailProductoName = detailCertBase?.productName || '—';
  const detailSubscriptionStatus = detailSub?.status || detailCertBase?.status || '—';
  const detailSubscriptionStart = detailSub?.start_date || detailCertBase?.issueDate || '—';
  const detailIssueDate = certificateDetail?.fecha_emision || detailCertBase?.issueDate || '—';
  const detailCoverageStart = certificateDetail?.fecha_inicio_cobertura || detailCertBase?.issueDate || detailSub?.start_date || '—';
  const detailCoverageEnd = certificateDetail?.fecha_termino_cobertura || detailCertBase?.expiryDate || detailSub?.end_date || '—';

  /* ── render ── */
  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Certificados</h1>
        <div className="flex gap-2">
          <Button onClick={() => fetchCertificates({ background: true })} variant="outline" disabled={loading || refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs — Cobertura */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Cobertura</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Total Certificados" value={allIndividualCerts.length} icon={<Award />} />
          <KpiCard
            label="Activos"
            value={allIndividualCerts.filter(c => c.status === 'active').length}
            icon={<ShieldCheck />}
            badge={{ text: 'activos', direction: 'neu' }}
          />
          <KpiCard
            label="Por Vencer (30d)"
            value={expiringCount}
            icon={<Clock />}
            badge={expiringCount > 0 ? { text: 'atención', direction: 'down' } : { text: 'ok', direction: 'neu' }}
          />
          <KpiCard
            label="Expirados"
            value={expiredCount}
            icon={<XCircle />}
            badge={expiredCount > 0 ? { text: 'crítico', direction: 'down' } : { text: 'ok', direction: 'neu' }}
          />
        </div>
      </div>

      {/* KPIs — Emisión */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Emisión</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Pendientes de Rendir"
            value={pendienteRendir}
            icon={<Clock />}
            badge={pendienteRendir > 0 ? { text: 'pendiente', direction: 'down' } : { text: 'ok', direction: 'neu' }}
          />
          <KpiCard
            label="Emitidos"
            value={emitidoCount}
            icon={<CheckCircle2 />}
            badge={{ text: 'emitidos', direction: 'neu' }}
          />
          <KpiCard
            label="Póliza Colectiva"
            value={colectivaCount}
            icon={<Users />}
            badge={{ text: 'colectiva', direction: 'neu' }}
          />
          <KpiCard
            label="Huérfanos"
            value={huerfanoCount}
            icon={<AlertCircle />}
            badge={huerfanoCount > 0 ? { text: 'sin proceso', direction: 'down' } : { text: 'ok', direction: 'neu' }}
          />
        </div>
      </div>

      {/* Table card */}
      <div className="yool-table-card overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-200/60 dark:border-slate-700/60">
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.35-4.35" />
            </svg>
            <Input
              placeholder="Buscar por cliente, email o nº serie…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-9 h-8 text-sm ${searchTerm ? 'pr-8' : ''}`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-8 text-sm"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="expiring_soon">Por Vencer</SelectItem>
              <SelectItem value="expired">Expirado</SelectItem>
              <SelectItem value="revoked">Revocado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={emisionFilter} onValueChange={setEmisionFilter}>
            <SelectTrigger className="w-52 h-8 text-sm"><SelectValue placeholder="Emisión" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (emisión)</SelectItem>
              <SelectItem value="pendiente_recepcion">Pendiente recepción</SelectItem>
              <SelectItem value="emitido">Emitido</SelectItem>
              <SelectItem value="error_envio">Error envío</SelectItem>
              <SelectItem value="huerfano">Huérfano</SelectItem>
              <SelectItem value="colectiva">Póliza colectiva</SelectItem>
            </SelectContent>
          </Select>
          <Select value={companiaFilter} onValueChange={setCompaniaFilter}>
            <SelectTrigger className="w-48 h-8 text-sm"><SelectValue placeholder="Compañía" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las compañías</SelectItem>
              {companiaOptions.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(searchTerm || statusFilter !== 'all' || emisionFilter !== 'all' || companiaFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); setEmisionFilter('all'); setCompaniaFilter('all'); }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/40 rounded px-2 h-8 transition-colors hover:border-foreground/40 whitespace-nowrap"
            >
              <X className="h-3 w-3" />Limpiar filtros
            </button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <div className="flex rounded border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
              <button
                onClick={() => setGroupBy('suscripcion')}
                className={`px-3 py-1.5 font-medium transition-colors ${groupBy === 'suscripcion' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'}`}
              >
                Por suscripción
              </button>
              <button
                onClick={() => setGroupBy('compania')}
                className={`px-3 py-1.5 font-medium border-l border-gray-200 dark:border-gray-700 transition-colors ${groupBy === 'compania' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'}`}
              >
                Por compañía
              </button>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {filteredCertificates.flatMap(g => g.certificates || []).length} cert.
            </span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cargando certificados…
          </div>
        ) : filteredCertificates.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title="Sin certificados"
            description={
              searchTerm || statusFilter !== 'all' || emisionFilter !== 'all' || companiaFilter !== 'all'
                ? 'No hay resultados para los filtros aplicados.'
                : 'Aún no hay certificados emitidos en el sistema.'
            }
            action={
              (searchTerm || statusFilter !== 'all' || emisionFilter !== 'all' || companiaFilter !== 'all')
                ? { label: 'Limpiar filtros', onClick: () => { setSearchTerm(''); setStatusFilter('all'); setEmisionFilter('all'); setCompaniaFilter('all'); } }
                : undefined
            }
          />
        ) : groupBy === 'compania' ? (
          <div className="relative w-full overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="w-6 h-10 px-4"></th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left">Compañía</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Total</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Pendientes</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Emitidos</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Error envío</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Huérfanos</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Colectivos</th>
                </tr>
              </thead>
              <tbody>
                {certsByCompania.map(([company, companyCerts]) => {
                  const isExp     = expandedRows.has(`co-${company}`);
                  const pending   = companyCerts.filter(c => c.estadoEmision === 'pendiente_recepcion').length;
                  const emitido   = companyCerts.filter(c => c.estadoEmision === 'emitido').length;
                  const errors    = companyCerts.filter(c => c.estadoEmision === 'error_envio').length;
                  const colectiva = companyCerts.filter(c => !!c.polizaColectivaId).length;
                  const huerfano  = companyCerts.filter(c => !c.estadoEmision && !c.polizaColectivaId).length;
                  const badge = (n, cls) => n > 0
                    ? <span className={`inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{n}</span>
                    : <span className="text-gray-300 text-xs">—</span>;
                  return (
                    <React.Fragment key={company}>
                      <tr
                        className="cursor-pointer border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                        onClick={() => toggleExpandedRow(`co-${company}`)}
                      >
                        <td className="w-6 text-gray-400 py-3 px-4">
                          {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{company}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">{companyCerts.length}</span>
                        </td>
                        <td className="py-3 px-4 text-center">{badge(pending,   'bg-yellow-50 text-yellow-700 border border-yellow-200')}</td>
                        <td className="py-3 px-4 text-center">{badge(emitido,   'bg-green-50 text-green-700 border border-green-200')}</td>
                        <td className="py-3 px-4 text-center">{badge(errors,    'bg-red-50 text-red-700 border border-red-200')}</td>
                        <td className="py-3 px-4 text-center">{badge(huerfano,  'bg-gray-100 text-gray-500 border border-gray-200')}</td>
                        <td className="py-3 px-4 text-center">{badge(colectiva, 'bg-blue-50 text-blue-700 border border-blue-200')}</td>
                      </tr>
                      {isExp && (
                        <tr className="cursor-default">
                          <td colSpan={8} className="bg-gray-50 dark:bg-gray-800/30 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="text-left text-gray-400 uppercase tracking-wider">
                                  <th className="px-3 py-2">Código</th>
                                  <th className="px-3 py-2">Cliente</th>
                                  <th className="px-3 py-2">Plan</th>
                                  <th className="px-3 py-2">Vencimiento</th>
                                  <th className="px-3 py-2">Estado emisión</th>
                                </tr>
                              </thead>
                              <tbody>
                                {companyCerts.map(cert => {
                                  const es = cert.estadoEmision;
                                  const el = es ? (EMISION_LABEL[es] || es) : (cert.polizaColectivaId ? 'Colectiva' : 'Huérfano');
                                  const ec = es ? (EMISION_BADGE[es] || EMISION_BADGE.huerfano) : (cert.polizaColectivaId ? EMISION_BADGE.colectiva : EMISION_BADGE.huerfano);
                                  return (
                                    <tr key={cert.id} className="border-t border-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/50">
                                      <td className="px-3 py-1.5 font-mono text-gray-500">{cert.certificateCodigo || cert.serialNumber || '—'}</td>
                                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{cert.clientName || '—'}</td>
                                      <td className="px-3 py-1.5 text-gray-500">{cert.planName || '—'}</td>
                                      <td className="px-3 py-1.5 text-gray-500">{cert.expiryDate ? String(cert.expiryDate).slice(0, 10) : '—'}</td>
                                      <td className="px-3 py-1.5"><span className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${ec}`}>{el}</span></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="relative w-full overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="w-6 text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left"></th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left">Cliente</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left">Compañía / Plan</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left">Cobertura</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-left">Emisión</th>
                  <th className="text-gray-600 dark:text-gray-400 font-semibold text-xs h-10 px-4 text-center">Certs</th>
                </tr>
              </thead>
              <tbody>
                {filteredCertificates.map((group) => (
                  <SubscriptionGroup
                    key={group.group_key || group.subscription_id}
                    group={group}
                    expanded={expandedRows.has(group.group_key || group.subscription_id)}
                    onToggle={toggleExpandedRow}
                    getStatusBadge={getStatusBadge}
                    getDaysUntilExpiry={getDaysUntilExpiry}
                    handleViewDetails={handleViewDetails}
                    handleDownloadCertificate={handleDownloadCertificate}
                    handleDownloadPdf={handleDownloadPdf}
                    setEmailModal={setEmailModal}
                    setEmailTo={setEmailTo}
                    pdfLoadingId={pdfLoadingId}
                    regeneratingId={regeneratingId}
                    openRegenerateModal={openRegenerateModal}
                    setWalletModal={setWalletModal}
                    setWalletProvider={setWalletProvider}
                    setWalletStep={setWalletStep}
                    setWalletEmail={setWalletEmail}
                    setWalletResult={setWalletResult}
                    setWalletError={setWalletError}
                    setCancelEmailModal={setCancelEmailModal}
                    setCancelEmailTo={setCancelEmailTo}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {/* ══════════════════════════════════════════════════
        MODAL: Detalle del Certificado
    ══════════════════════════════════════════════════ */}
    {detailOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 pointer-events-auto" onClick={closeDetail}>
        <div
          className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden pointer-events-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* ── Header con logos ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
            <img src={YOOL_LOGO} alt="Yool Digital" className="h-7 object-contain" onError={e => { e.target.style.display='none'; }} />
            <div className="flex items-center gap-3">
              {detailCompanyName !== '—' && (
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{detailCompanyName}</span>
              )}
              <button onClick={closeDetail} className="ml-2 rounded-full p-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="overflow-y-auto flex-1">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="h-6 w-6 animate-spin mr-3 text-[#007D6E]" />
                <span className="text-sm text-muted-foreground">Cargando detalle…</span>
              </div>
            ) : (
              <div className="p-6 space-y-6">

                {/* Certificado title */}
                <div className="flex items-start gap-4 pb-2">
                  <div className="rounded-xl bg-[#007D6E]/10 p-3 shrink-0">
                    <Award className="h-7 w-7 text-[#007D6E]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Certificado de Cobertura</h2>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      {certificateDetail?.codigo || detailCertBase?.serialNumber || '—'}
                    </p>
                    {detailPlanName !== '—' && (
                      <p className="text-sm text-[#007D6E] font-medium mt-1">{detailPlanName}</p>
                    )}
                  </div>
                  <div className="ml-auto shrink-0">
                    {detailCertBase && getStatusBadge(detailCertBase.status, detailCertBase.expiryDate, detailCertBase.estadoCertificado, detailCertBase.coberturaHasta)}
                  </div>
                </div>

                {/* Titular */}
                <Section icon={User} title="Titular">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                    <DetailRow label="Nombre"      value={detailNombre} />
                    <DetailRow label="RUT"         value={detailRut} />
                    <DetailRow label="Email"       value={detailEmail} />
                    <DetailRow label="Plan"        value={detailPlanName} />
                    <DetailRow label="Producto"    value={detailProductoName} />
                    {detailCompanyName !== '—' && (
                      <DetailRow label="Compañía"  value={detailCompanyName} />
                    )}
                  </div>
                </Section>

                {/* Cargas / beneficiarios */}
                {detailCargas.length > 0 && (
                  <Section icon={Users} title={`Cargas / Beneficiarios (${detailCargas.length})`}>
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-800/60 text-left">
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Nombre</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">RUT</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tipo</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Edad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailCargas.map((carga, i) => (
                            <tr key={carga.id || i} className="border-t border-slate-100 dark:border-slate-800">
                              <td className="px-4 py-2.5 font-medium">
                                {[carga.nombre, carga.apellido_paterno, carga.apellido_materno].filter(Boolean).join(' ') || '—'}
                              </td>
                              <td className="px-4 py-2.5 text-muted-foreground"><MonoText>{carga.rut || '—'}</MonoText></td>
                              <td className="px-4 py-2.5">
                                <Badge variant="outline" className="text-xs capitalize">{carga.tipo || '—'}</Badge>
                              </td>
                              <td className="px-4 py-2.5 text-muted-foreground">{carga.edad != null ? `${carga.edad} años` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}

                {/* Cobertura y precios */}
                <Section icon={Calendar} title="Cobertura">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                    <DetailRow label="Inicio de cobertura"   value={fmtDate(detailCoverageStart)} />
                    <DetailRow label="Término de cobertura"  value={fmtDate(detailCoverageEnd)} />
                    {detailCertBase?.coberturaHasta && detailCertBase?.estadoCertificado && detailCertBase.estadoCertificado !== 'vigente' && (
                      <DetailRow label="Cobertura válida hasta" value={fmtDate(detailCertBase.coberturaHasta)} />
                    )}
                    <DetailRow label="Fecha emisión"         value={fmtDate(detailIssueDate)} />
                    <DetailRow label="Inicio suscripción"    value={fmtDate(detailSubscriptionStart)} />
                  </div>
                </Section>

                {/* Primas por asegurado */}
                <Section icon={CreditCard} title="Primas por Asegurado">
                  {detailCargas.length > 0 || titularPrimaUF != null ? (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-800/60 text-left">
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Asegurado</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tipo</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground text-right">Prima mensual</th>
                          </tr>
                        </thead>
                        <tbody>
                          {titularPrimaUF != null && (
                            <tr className="border-t border-slate-100 dark:border-slate-800">
                              <td className="px-4 py-2.5">
                                <div className="font-medium text-slate-800 dark:text-slate-200">{detailNombre}</div>
                                <div className="text-[11px] text-muted-foreground">{detailRut !== '—' ? detailRut : ''}</div>
                              </td>
                              <td className="px-4 py-2.5">
                                <Badge variant="outline" className="text-xs capitalize">Titular</Badge>
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm font-medium">
                                {fmtUFDetailed(titularPrimaUF)}
                              </td>
                            </tr>
                          )}
                          {primasCargas.map(({ carga, primaUF, tipoCarga }, i) => {
                            const nombre = [carga.nombre, carga.apellido_paterno, carga.apellido_materno].filter(Boolean).join(' ') || '—';
                            return (
                              <tr key={carga.id || i} className="border-t border-slate-100 dark:border-slate-800">
                                <td className="px-4 py-2.5">
                                  <div className="font-medium text-slate-800 dark:text-slate-200">{nombre}</div>
                                  <div className="text-[11px] text-muted-foreground">{carga.rut || ''}</div>
                                </td>
                                <td className="px-4 py-2.5">
                                  <Badge variant="outline" className="text-xs capitalize">{tipoCarga}</Badge>
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono text-sm font-medium">
                                  {fmtUFDetailed(primaUF)}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Total row */}
                          <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-[#F0FBF9] dark:bg-[#007D6E]/10">
                            <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300" colSpan={2}>
                              Prima Total Mensual
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-[#007D6E] font-mono">
                              {totalPrimaResumenUF != null ? fmtUFDetailed(totalPrimaResumenUF) : '—'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    /* Fallback when no cargas — show flat amounts */
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                      <DetailRow label="Prima mensual" value={totalPrimaResumenUF != null ? fmtUF(totalPrimaResumenUF) : '—'} />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-1 mt-2">
                    <DetailRow label="Estado suscripción" value={detailSubscriptionStatus} />
                  </div>
                </Section>

                {/* Compañía aseguradora */}
                {detailCompanyName !== '—' && (
                  <Section icon={Building2} title="Compañía Aseguradora">
                    <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                      <div className="space-y-1">
                        <p className="text-sm font-semibold">{detailCompanyName}</p>
                      </div>
                    </div>
                  </Section>
                )}

              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <Button variant="outline" onClick={closeDetail}>Cerrar</Button>
            <div className="flex gap-2">
              <Button
                onClick={() => detailCertBase && handleDownloadCertificate(detailCertBase)}
                disabled={!detailCertBase?.archiveId}
                className="bg-[#007D6E] hover:bg-[#006558] text-white"
              >
                <FileDown className="h-4 w-4 mr-2" />Descargar Certificado
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════
        MODAL: Email PDF
    ══════════════════════════════════════════════════ */}
    {emailModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto" onClick={() => { setEmailModal(null); setEmailTo(''); }}>
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4 pointer-events-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#007D6E]" />
            <h2 className="font-semibold text-base">Enviar certificado por email</h2>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              <span className="font-semibold">ℹ️ Información:</span> Se enviará el archivo del certificado actualmente en la base de datos. Si necesitas actualizar el archivo, regenera el certificado primero.
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Destinatario</label>
            <Input
              type="email"
              placeholder="cliente@email.com"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendPdfEmail()}
              className="h-9"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={handleSendPdfEmail} disabled={sendingEmail || !emailTo}>
              {sendingEmail
                ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />Enviando...</>
                : <><Mail className="h-3.5 w-3.5 mr-2" />Enviar PDF</>}
            </Button>
            <Button variant="outline" onClick={() => { setEmailModal(null); setEmailTo(''); }} disabled={sendingEmail}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════
        MODAL: Enviar email de baja (certificado anulado)
    ══════════════════════════════════════════════════ */}
    {cancelEmailModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto" onClick={() => { setCancelEmailModal(null); setCancelEmailTo(''); }}>
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4 pointer-events-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-[#007D6E]" />
            <h2 className="font-semibold text-base">Enviar email de baja</h2>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400">
            Se enviará el email de cancelación con el certificado anulado (PDF) adjunto a:
          </p>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Destinatario</label>
            <Input
              type="email"
              placeholder="cliente@email.com"
              value={cancelEmailTo}
              onChange={e => setCancelEmailTo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendCancelEmail()}
              className="h-9"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={handleSendCancelEmail} disabled={cancelEmailSending || !cancelEmailTo}>
              {cancelEmailSending
                ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />Enviando...</>
                : <><Mail className="h-3.5 w-3.5 mr-2" />Enviar</>}
            </Button>
            <Button variant="outline" onClick={() => { setCancelEmailModal(null); setCancelEmailTo(''); }} disabled={cancelEmailSending}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════
        MODAL: Regenerar Certificado
    ══════════════════════════════════════════════════ */}
    {regenerateModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto" onClick={closeRegenerateModal}>
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4 pointer-events-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-[#007D6E]" />
            <h2 className="font-semibold text-base">Regenerar certificado</h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            ¿Estás seguro de que deseas regenerar este certificado? Se creará una nueva versión con la fecha de emisión actualizada y se generará un PDF con las condiciones de cobertura.
          </p>
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-[#007D6E] hover:bg-[#006558] text-white"
              onClick={confirmRegenerateCertificate}
              disabled={regeneratingId === regenerateModal}
            >
              {regeneratingId === regenerateModal
                ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />Regenerando...</>
                : <>Sí, regenerar</>}
            </Button>
            <Button variant="outline" onClick={closeRegenerateModal} disabled={regeneratingId === regenerateModal}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════
        MODAL: Emitir Wallet Pass
    ══════════════════════════════════════════════════ */}
    {walletModal && (() => {
      const STEPS = [
        { label: 'Validando certificado', sub: 'Datos del titular y cobertura' },
        { label: `Generando pass ${walletProvider === 'apple' ? 'Apple' : 'Google'} Wallet`, sub: 'Comunicando con el servicio' },
        { label: walletEmail ? 'Enviando email al titular' : 'Finalizando emisión', sub: walletEmail || 'Completando proceso' },
      ];
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Wallet className="h-4.5 w-4.5 text-[#007D6E]" />
                <h2 className="font-semibold text-base">Emitir Wallet Pass</h2>
              </div>
              {walletStep !== 'issuing' && (
                <button onClick={closeWalletModal} className="rounded-full p-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              )}
            </div>

            <div className="p-5 space-y-4">

              {/* ── STATE: IDLE ── */}
              {walletStep === null && (
                <>
                  {/* Cert info */}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3 space-y-0.5">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{walletModal.clientName}</p>
                    <p className="text-xs text-muted-foreground font-mono">{walletModal.serialNumber}</p>
                    {walletModal.companyName && walletModal.companyName !== '—' && (
                      <p className="text-xs text-muted-foreground">{walletModal.companyName}</p>
                    )}
                  </div>

                  {/* Provider */}
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">Plataforma</label>
                    <Select value={walletProvider} onValueChange={setWalletProvider}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="apple">Apple Wallet</SelectItem>
                        <SelectItem value="google">Google Wallet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                      Enviar email al titular
                      <span className="ml-1.5 font-normal normal-case">(opcional)</span>
                    </label>
                    <Input
                      type="email"
                      placeholder="cliente@email.com"
                      value={walletEmail}
                      onChange={e => setWalletEmail(e.target.value)}
                      className="h-9"
                    />
                    {walletEmail && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Se enviará el enlace del pass por correo
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <Button className="flex-1 bg-[#007D6E] hover:bg-[#006558] text-white" onClick={handleWalletIssue}>
                      <Wallet className="h-3.5 w-3.5 mr-2" />Emitir Pass
                    </Button>
                    <Button variant="outline" onClick={closeWalletModal}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}

              {/* ── STATE: ISSUING ── */}
              {walletStep === 'issuing' && (
                <div className="py-2 space-y-0">
                  {STEPS.map((step, i) => {
                    const done    = i < walletSubstep;
                    const active  = i === walletSubstep;
                    const waiting = i > walletSubstep;
                    return (
                      <div key={i} className={`flex items-start gap-3 py-3 transition-opacity duration-500 ${waiting ? 'opacity-30' : 'opacity-100'}`}>
                        <div className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                          done   ? 'bg-[#007D6E]' :
                          active ? 'border-2 border-[#007D6E]' :
                                   'border-2 border-slate-200 dark:border-slate-700'
                        }`}>
                          {done   && <Check className="h-3 w-3 text-white" />}
                          {active && <div className="h-2 w-2 rounded-full bg-[#007D6E] animate-pulse" />}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${active ? 'text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>
                            {step.label}
                          </p>
                          {step.sub && (
                            <p className="text-xs text-muted-foreground truncate">{step.sub}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── STATE: DONE ── */}
              {walletStep === 'done' && (
                <>
                  <div className="flex flex-col items-center gap-2 py-4 text-center">
                    <div className="rounded-full bg-[#007D6E]/10 p-3 mb-1">
                      <CheckCircle2 className="h-10 w-10 text-[#007D6E]" />
                    </div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100">Pass emitido correctamente</p>
                    {walletResult?.serial_number && (
                      <p className="text-xs font-mono text-muted-foreground bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded">
                        {walletResult.serial_number}
                      </p>
                    )}
                    {walletEmail && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {walletResult?.email_sent
                          ? `Email enviado a ${walletEmail}`
                          : walletResult?.email_error
                            ? `No se pudo enviar el email (${walletResult.email_error})`
                            : `Email procesado para ${walletEmail}`}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {walletProvider === 'apple' && walletResult?.pkpass_url && (
                      <Button
                        className="flex-1 bg-[#1C1C1E] hover:bg-black text-white"
                        onClick={() => window.open(walletResult.pkpass_url, '_blank')}
                      >
                        <Wallet className="h-3.5 w-3.5 mr-2" />Abrir en Wallet
                      </Button>
                    )}
                    <Button variant="outline" className="flex-1" onClick={closeWalletModal}>
                      Cerrar
                    </Button>
                  </div>
                </>
              )}

              {/* ── STATE: ERROR ── */}
              {walletStep === 'error' && (
                <>
                  <div className="flex flex-col items-center gap-2 py-4 text-center">
                    <div className="rounded-full bg-destructive/10 p-3 mb-1">
                      <AlertCircle className="h-10 w-10 text-destructive" />
                    </div>
                    <p className="font-semibold text-destructive">Error al emitir el pass</p>
                    <p className="text-xs text-muted-foreground max-w-[260px] break-words">{walletError}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-[#007D6E] hover:bg-[#006558] text-white" onClick={() => setWalletStep(null)}>
                      Reintentar
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={closeWalletModal}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
};

export default CertificatesList;
